import React, { useState, useEffect } from 'react';
import { FiMessageSquare } from 'react-icons/fi';
import API from '../utils/api';

const BookingDetailModal = ({ booking, onClose, onStatusChange }) => {
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [fieldNotes, setFieldNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);

  useEffect(() => {
    if (booking) {
      fetchFieldNotes(booking.task_id || booking.id);
    }
  }, [booking]);

  const fetchFieldNotes = async (idToUse) => {
    try {
      setIsLoadingNotes(true);
      console.log(`Fetching field notes for ID: ${idToUse}`);

      const response = await API.get(`/api/mechanic-tasks/${idToUse}`);
      console.log('Field notes response:', response.data);

      if (response.data && response.data.field_notes) {
        setFieldNotes(response.data.field_notes);
      } else {
        // Jika tidak ada field_notes, atur sebagai array kosong
        setFieldNotes([]);
      }

      setIsLoadingNotes(false);
    } catch (error) {
      console.error('Error fetching field notes:', error);
      // Tampilkan error sebagai bagian dari UI, bukan hanya di konsol
      setFieldNotes([{
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0],
        note: `Gagal memuat catatan: ${error.response?.data?.message || error.message}`
      }]);
      setIsLoadingNotes(false);
    }
  };

  if (!booking) return null;

  const getStatusBadge = (status) => {
    const badgeClasses = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'in-progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200',
    };

    const statusText = {
      'pending': 'Menunggu',
      'in-progress': 'Sedang Dikerjakan',
      'completed': 'Selesai',
      'cancelled': 'Dibatalkan'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${badgeClasses[status] || 'bg-gray-100'}`}>
        {statusText[status] || status}
      </span>
    );
  };

  const handleSaveNotes = async () => {
    if (!notes.trim()) {
      alert('Catatan tidak boleh kosong');
      return;
    }

    try {
      setIsSaving(true);

      // Use booking ID if task_id is not available
      const idToUse = booking.task_id || booking.id;
      console.log(`Saving note for ID: ${idToUse}`);

      // Use the new endpoint for adding field notes
      const response = await API.post(`/api/mechanic-tasks/${idToUse}/notes`, {
        note: notes
      });

      console.log('Note saved successfully:', response.data);

      // Refresh field notes
      fetchFieldNotes(idToUse);

      // Clear the input
      setNotes('');

      alert('Catatan berhasil disimpan');
      setIsSaving(false);
    } catch (err) {
      console.error('Error saving notes:', err);
      const errorMessage = err.response?.data?.message || 'Kesalahan server saat menyimpan catatan';
      alert(`Gagal menyimpan catatan: ${errorMessage}`);
      setIsSaving(false);
    }
  };

  const formatDateTime = (date, time) => {
    if (!date) return '';

    try {
      return new Date(date + 'T' + time).toLocaleString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return `${date} ${time || ''}`;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl p-6 overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <h3 className="text-xl font-semibold">Detail Booking</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-red-600 text-lg">&times;</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-medium mb-2">Informasi Layanan</h4>
            <div className="mb-2">
              <span className="text-gray-500 text-sm">Jenis Layanan:</span>
              <p className="font-medium">{booking.service_name}</p>
            </div>
            <div className="mb-2">
              <span className="text-gray-500 text-sm">Tanggal & Waktu:</span>
              <p>{booking.booking_date} pukul {booking.booking_time}</p>
            </div>
            <div className="mb-2">
              <span className="text-gray-500 text-sm">Estimasi Waktu:</span>
              <p>{booking.estimated_time} menit</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Status:</span>
              <div className="mt-1">
                {getStatusBadge(booking.task_status)}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded">
            <h4 className="font-medium mb-2">Informasi Pelanggan & Kendaraan</h4>
            <div className="mb-2">
              <span className="text-gray-500 text-sm">Nama Pelanggan:</span>
              <p className="font-medium">{booking.customer_name}</p>
            </div>
            <div className="mb-2">
              <span className="text-gray-500 text-sm">Kendaraan:</span>
              <p>{booking.make} {booking.model}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">Plat Nomor:</span>
              <p>{booking.license_plate}</p>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Catatan Pelanggan</h4>
          <div className="bg-gray-50 p-3 rounded">
            {booking.customer_notes || 'Tidak ada catatan dari pelanggan'}
          </div>
        </div>

        {/* Field Notes History */}
        <div className="mb-4">
          <h4 className="font-medium mb-2">Riwayat Catatan Lapangan</h4>
          {isLoadingNotes ? (
            <div className="bg-gray-50 p-3 rounded flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : fieldNotes.length > 0 ? (
            <div className="bg-gray-50 p-3 rounded max-h-40 overflow-y-auto">
              {fieldNotes.map((note, index) => (
                <div key={index} className="mb-2 pb-2 border-b border-gray-200 last:border-b-0">
                  <div className="flex items-start">
                    <FiMessageSquare className="text-primary mt-1 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500">
                        {note.date && formatDateTime(note.date, note.time)}
                      </p>
                      <p>{note.note}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 p-3 rounded text-gray-500">
              Belum ada catatan lapangan
            </div>
          )}
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Tambah Catatan Lapangan</h4>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-gray-300 rounded p-3 h-24"
            placeholder="Tulis catatan pekerjaan di sini..."
          />
          <button
            onClick={handleSaveNotes}
            disabled={isSaving}
            className={`mt-2 ${isSaving ? 'bg-gray-400' : 'bg-primary'} text-white px-4 py-2 rounded`}
          >
            {isSaving ? 'Menyimpan...' : 'Simpan Catatan'}
          </button>
        </div>

        <div className="border-t pt-4 flex justify-between">
          <div>
            {booking.task_status === 'pending' && (
              <button
                onClick={() => onStatusChange(booking.id, 'in-progress')}
                className="bg-blue-500 text-white px-4 py-2 rounded mr-2"
              >
                Mulai Pekerjaan
              </button>
            )}
            {booking.task_status === 'in-progress' && (
              <button
                onClick={() => onStatusChange(booking.id, 'completed')}
                className="bg-green-500 text-white px-4 py-2 rounded"
              >
                Selesaikan Pekerjaan
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-800 px-4 py-2 rounded"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailModal; 